type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    shiftwagonAnalyticsLoaded?: boolean;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();

export function initializeAnalytics(): void {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;
  if (window.shiftwagonAnalyticsLoaded) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer?.push(arguments);
    };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
  window.shiftwagonAnalyticsLoaded = true;
}

/**
 * A named interaction, for questions page views cannot answer — chiefly whether
 * anyone prices a route, and whether pricing one leads to an install.
 *
 * NEVER PASS PERSONAL DATA HERE. No addresses, no coordinates, no names, no
 * contact details. The estimate widget handles the addresses people type, and
 * those must not leave for an analytics vendor: send the shape of the trip
 * (distance, vehicle class, whether it exceeded the instant limit), never the
 * trip itself.
 *
 * Note the site's privacy policy does not currently disclose analytics at all,
 * which predates this function — see docs/ux-alignment.
 */
export function trackEvent(
  name: string,
  params: Record<string, string | number | boolean> = {},
): void {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;

  initializeAnalytics();
  window.gtag?.('event', name, params);
}

export function trackPageView(path: string): void {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;

  initializeAnalytics();

  window.gtag?.('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: path,
  });
}
